import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormArray, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isEmpty } from 'lodash';
import { concatMap, forkJoin, from, of } from 'rxjs';
import { CompanyForm } from 'src/app/classes/form-groups/company-form';
import { CompanyVendorForm } from 'src/app/classes/form-groups/company-vendor-form';
import { SmallBusinessOptionForm } from 'src/app/classes/form-groups/small-business-option-form';
import { VendorForm } from 'src/app/classes/form-groups/vendor-form';
import { VendorOperatingStatesForm } from 'src/app/classes/form-groups/vendor-operating-states-form';
import { CompanyOffice, Vendor } from 'src/app/classes/models';
import { SmallBusinessOption } from 'src/app/classes/models/small-business-option';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { State } from 'src/app/interfaces/state';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AddressService } from 'src/app/services/address.service';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorOfficeService } from 'src/app/services/company-vendor-office.service';
import { SmallBusinessOptionService } from 'src/app/services/small-business-option.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorTradeStateService } from 'src/app/services/vendor-trade-state.service';
import { VendorService } from 'src/app/services/vendor.service';
import { VendorEditComponent } from 'src/app/shared-components/vendor-edit/vendor-edit.component';
import { vendorsService } from 'src/app/shared/vive-api/vendors.service';
import { AddressDto, VendorDto } from 'src/app/shared/vive-api/vive-api-models';
import { einSsnValidator } from 'src/app/validators/ein-ssn-validator';
import {
  isLegalNameLockedForUser,
  vendorNameLengthModalInputs
} from 'src/app/validators/vendor-name-length-validator';

@Component({
  selector: 'app-add-edit-vendor-information-modal',
  templateUrl: './add-edit-vendor-information-modal.component.html',
  styleUrls: ['./add-edit-vendor-information-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditVendorInformationModalComponent implements OnInit {
  private resignOriginalValues: Partial<
    Pick<Vendor, 'legalName' | 'dba' | 'companyType' | 'llcType'>
  > = {};

  constructor(
    private ActiveModal: NgbActiveModal,
    private VendorService: VendorService,
    private UtilityService: UtilityService,
    private ToastService: ToastService,
    private AddressService: AddressService,
    private SmallBusinessOptionService: SmallBusinessOptionService,
    private CompanyVendorOfficeService: CompanyVendorOfficeService,
    private VendorTradeStateService: VendorTradeStateService,
    private cdr: ChangeDetectorRef,
    private ModalService: NgbModal,
    private AuthService: AuthService
  ) {
    this.VendorFormGroup.setControl(
      'CompanyVendors',
      new FormArray<CompanyVendorForm>([])
    );
  }

  @Input() VendorId!: number;
  @Input() CompanyId!: number;

  StreetAddress?: AddressDto;
  MailingAddress?: AddressDto;
  RemitToAddress?: AddressDto;

  VendorFormGroup = new VendorForm();
  localizationInfo!: LocalizationInfo;
  OperatingStatesForm = new VendorOperatingStatesForm();
  selectedSmallBusinessOptionsFormArray =
    new FormArray<SmallBusinessOptionForm>([]);

  CompanyIdToCompanyOfficesMap = new Map<number, CompanyOffice[]>([]);

  // Maps CompanyId to the CompanyVendor record.
  // There should only ever be 1 CompanyVendor record per Client per Vendor.
  ClientCompanyVendorMap = new Map<number, CompanyVendorForm>();

  // The editor loads the agreement and multi-client flags that lock the legal
  // name; this component's own vendor query does not select them.
  @ViewChild(VendorEditComponent) VendorEdit?: VendorEditComponent;

  updateVendorFormControlsForCountry(country: 'US' | 'CA') {
    if (country === 'US') {
      this.VendorFormGroup.controls['isPubliclyTraded'].setValidators([
        Validators.required
      ]);
      this.VendorFormGroup.addValidators(einSsnValidator);
    } else if (country === 'CA') {
      this.VendorFormGroup.controls['bsn'].setValidators([Validators.required]);
    } else {
      throw this.ToastService.addErrorToast(
        'Error',
        'Country is not supported',
        { returnError: true }
      );
    }

    this.VendorFormGroup.updateValueAndValidity();
  }

  sinChanged() {
    if (this.VendorFormGroup.controls['usesSIN'].value) {
      this.VendorFormGroup.controls['bsn'].disable();
      this.VendorFormGroup.controls['bsn'].patchValue(null);
    } else {
      this.VendorFormGroup.controls['bsn'].enable();
    }
  }

  afterHoursPhoneRequirement() {
    const allowAfterHoursService =
      this.VendorFormGroup.controls['allowAfterHoursService'].value;
    const afterHoursPhone = this.VendorFormGroup.controls['afterHoursPhone'];

    if (allowAfterHoursService) {
      afterHoursPhone.setValidators(Validators.required);
    } else {
      afterHoursPhone.markAsPristine();
      afterHoursPhone.removeValidators(Validators.required);
      afterHoursPhone.setErrors(null);
    }

    afterHoursPhone.updateValueAndValidity();
  }

  close() {
    this.ActiveModal.dismiss();
  }

  save(resignConfirmed = false, operatingStatesConfirmed = false) {
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.VendorFormGroup
    );

    if (errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There is an error with a field below.',
        { returnError: true }
      );
    }

    const role = this.AuthService.getUser().UserAccountRole?.role;
    // saveVendor discards everything but qtyEmployees for the insurance team,
    // so a name they submit is never written and must not block them.
    if (role !== 'ADMIN_INSURANCE_TEAM') {
      const { legalName, dba } = this.VendorFormGroup.getRawValue();
      const modalInputs = vendorNameLengthModalInputs(
        { legalName, dba },
        {
          legalNameLockedForUser: isLegalNameLockedForUser(
            this.VendorEdit?.Vendor,
            role
          )
        }
      );
      if (modalInputs) {
        const { componentInstance } = this.ModalService.open(
          ConfirmModalComponent,
          { centered: true }
        );
        Object.assign(componentInstance, modalInputs);
        return;
      }
    }

    if (!resignConfirmed) {
      const updated = this.VendorFormGroup.getRawValue() as Vendor;
      const willRequireResign = (
        ['legalName', 'dba', 'companyType', 'llcType'] as const
      ).some(
        (field) =>
          (this.resignOriginalValues[field] ?? null) !==
          (updated[field] ?? null)
      );

      if (willRequireResign) {
        const { componentInstance, closed } = this.ModalService.open(
          ConfirmModalComponent,
          { backdrop: 'static', modalDialogClass: 'confirm-modal' }
        );
        Object.assign(componentInstance, {
          title: 'Vendor will need to re-sign',
          displayText:
            'Changing the legal name, DBA, or tax classification will require this vendor to re-sign their Vendor Service Agreement and submit a new W-9 with all of their active clients. Their existing signed documents will be archived until they re-sign. Continue?',
          continueButtonText: 'Save & require re-sign'
        });
        closed.subscribe((proceed: boolean) => {
          if (proceed) {
            this.save(true);
          }
        });
        return;
      }
    }

    if (!operatingStatesConfirmed && this.OperatingStatesForm.dirty) {
      this.VendorTradeStateService.confirmOperatingStateChanges(
        this.OperatingStatesForm
      ).subscribe((proceed: boolean) => {
        if (proceed) {
          this.save(true, true);
        }
      });
      return;
    }

    const {
      MailingAddress: MailingAddressFormGroup,
      StreetAddress: StreetAddressFormGroup,
      RemitToAddress: RemitToAddressFormGroup,
      CompanyVendors: CompanyVendorsFormArray
    } = this.VendorFormGroup.controls;

    const observables = [
      this.VendorService.saveVendor(
        this.VendorFormGroup.getRawValue() as Vendor
      )
    ];

    if (
      !MailingAddressFormGroup.controls['isMailing'].value &&
      this.MailingAddress &&
      this.MailingAddress.id !== this.StreetAddress?.id
    ) {
      if (!this.MailingAddress.id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Mailing Address is missing id.',
          { returnError: true }
        );
      }
      observables.push(
        this.AddressService.deleteAddress(this.MailingAddress.id)
      );
    }

    if (
      !RemitToAddressFormGroup.controls['isRemitTo'].value &&
      this.RemitToAddress &&
      this.RemitToAddress.id !== this.MailingAddress?.id
    ) {
      if (!this.RemitToAddress.id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Mailing Address is missing id.',
          { returnError: true }
        );
      }
      observables.push(
        this.AddressService.deleteAddress(this.RemitToAddress.id)
      );
    }

    if (this.selectedSmallBusinessOptionsFormArray.dirty) {
      observables.push(
        this.SmallBusinessOptionService.saveSmallBusinessOptions(
          this.VendorId,
          this.selectedSmallBusinessOptionsFormArray.getRawValue() as SmallBusinessOption[]
        )
      );
    }

    const companyVendorOfficeObservables =
      this.CompanyVendorOfficeService.saveDirtySelections(
        CompanyVendorsFormArray!,
        this.VendorId!
      );

    // Make sure this forkJoin finishes correctly. Need both observables to emit 'complete' event.
    if (companyVendorOfficeObservables.length > 0) {
      observables.push(forkJoin(companyVendorOfficeObservables));
    }

    // Runs after the Client Offices save so it builds on any states that
    // save added.
    const save$ = forkJoin(observables).pipe(
      concatMap(() =>
        this.OperatingStatesForm.dirty
          ? this.VendorTradeStateService.saveOperatingStates(
              this.VendorId!,
              this.OperatingStatesForm
            )
          : of(null)
      )
    );

    // The addresses save one at a time, each after the previous one resolves.
    save$
      .pipe(
        concatMap(() =>
          from(
            vendorsService.updateOne({
              pathParams: { vendorId: this.VendorId },
              data: { StreetAddress: StreetAddressFormGroup.getRawValue() }
            })
          )
        ),
        concatMap(({ StreetAddress }) => {
          this.StreetAddress = StreetAddress;

          return MailingAddressFormGroup.controls['isMailing'].value
            ? from(
                vendorsService.updateOne({
                  pathParams: { vendorId: this.VendorId },
                  data: {
                    MailingAddress: MailingAddressFormGroup.getRawValue()
                  }
                })
              )
            : of<Partial<VendorDto>>({});
        }),
        concatMap(({ MailingAddress }) => {
          if (!isEmpty(MailingAddress)) {
            this.MailingAddress = MailingAddress;
          }

          return RemitToAddressFormGroup.controls['isRemitTo'].value
            ? from(
                vendorsService.updateOne({
                  pathParams: { vendorId: this.VendorId },
                  data: {
                    RemitToAddress: RemitToAddressFormGroup.getRawValue()
                  }
                })
              )
            : of<Partial<VendorDto>>({});
        })
      )
      .subscribe({
        next: ({ RemitToAddress }) => {
          if (!isEmpty(RemitToAddress)) {
            this.RemitToAddress = RemitToAddress;
          }

          this.cdr.markForCheck();
          this.ActiveModal.close(this.VendorFormGroup.value);
          this.ToastService.addSuccessToast(
            'Success',
            'Successfully saved Vendor information.'
          );
        }
      });
  }

  ngOnInit(): void {
    this.VendorService.getVendorV2(this.VendorId, {
      excludedAttributes: JSON.stringify(['logo']),
      SmallBusinessOption: JSON.stringify({
        options: { required: false },
        attributes: ['id', 'VendorId', 'type']
      }),
      StreetAddress: JSON.stringify({}),
      MailingAddress: JSON.stringify({}),
      RemitToAddress: JSON.stringify({}),
      CompanyVendor: JSON.stringify({
        attributes: [
          'id',
          'CompanyId',
          'VendorId',
          'country',
          'CompanyComplianceTypeId'
        ],
        CompanyId: this.CompanyId,
        Company: {
          attributes: ['id', 'legalName'],
          CompanyOffice: {}
        },
        CompanyVendorOffice: {
          CompanyOffice: {}
        }
      })
    }).subscribe({
      next: (Vendor: Vendor | null) => {
        if (!Vendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Vendor information.',
            { returnError: true }
          );
        }

        const {
          country,
          StreetAddress,
          MailingAddress,
          RemitToAddress,
          SmallBusinessOptions,
          CompanyVendors
        } = Vendor;

        if (!country) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Country information.',
            { returnError: true }
          );
        }

        this.localizationInfo =
          this.UtilityService.getLocalizedValuesMap(country);

        // Fire off sinChanged event for Canadian vendors to update the GST/HST (bsn) form control status.
        if (country === 'CA') {
          this.sinChanged();
        }

        this.updateVendorFormControlsForCountry(country);

        // Set the validation on the after hours phone input if allowAfterHoursService is true.
        this.afterHoursPhoneRequirement();

        if (
          !Array.isArray(CompanyVendors) ||
          (Array.isArray(CompanyVendors) && CompanyVendors.length === 0)
        ) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor relationship information.',
            { returnError: true }
          );
        }

        CompanyVendors.forEach((CompanyVendor) => {
          const { CompanyId, Company, CompanyVendorOffices } = CompanyVendor;

          if (!CompanyId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Client Id.',
              { returnError: true }
            );
          }

          if (!Company) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Client information.',
              { returnError: true }
            );
          }

          const CompanyVendorFormGroup = new CompanyVendorForm();
          const CompanyFormGroup = new CompanyForm();

          const RequiredCompanyVendorFormControls = new Set([
            'id',
            'CompanyId',
            'VendorId',
            'CompanyVendorOffices'
          ]);
          Object.keys(CompanyVendorFormGroup.controls).forEach(
            (controlField) => {
              if (!RequiredCompanyVendorFormControls.has(controlField)) {
                CompanyVendorFormGroup.removeControl(controlField);
              }
            }
          );

          const RequiredCompanyFormControls = new Set([
            'id',
            'legalName',
            'CompanyOffices'
          ]);
          Object.keys(CompanyFormGroup.controls).forEach((controlField) => {
            if (!RequiredCompanyFormControls.has(controlField)) {
              CompanyFormGroup.removeControl(controlField);
            }
          });

          CompanyFormGroup.patchValue(Company);
          CompanyVendorFormGroup.setControl('Company', CompanyFormGroup);
          CompanyVendorFormGroup.patchValue(CompanyVendor);

          const { CompanyOffices } = Company;
          if (CompanyOffices) {
            this.CompanyIdToCompanyOfficesMap.set(CompanyId, CompanyOffices);
          }

          this.ClientCompanyVendorMap.set(CompanyId, CompanyVendorFormGroup);
          this.VendorFormGroup.controls['CompanyVendors']!.push(
            CompanyVendorFormGroup
          );
        });

        this.VendorFormGroup.patchValue(Vendor);

        // remember the loaded values of the re-sign trigger fields so
        // save() can warn before forcing a VSA re-sign.
        this.resignOriginalValues = {
          legalName: Vendor.legalName,
          dba: Vendor.dba,
          companyType: Vendor.companyType,
          llcType: Vendor.llcType
        };

        const {
          MailingAddress: MailingAddressFormGroup,
          StreetAddress: StreetAddressFormGroup,
          RemitToAddress: RemitToAddressFormGroup
        } = this.VendorFormGroup.controls;

        MailingAddressFormGroup.patchValue({
          country: country,
          VendorId: this.VendorId
        });
        StreetAddressFormGroup.patchValue({
          country: country,
          VendorId: this.VendorId
        });
        RemitToAddressFormGroup.patchValue({
          country: country,
          VendorId: this.VendorId
        });

        if (Array.isArray(SmallBusinessOptions)) {
          SmallBusinessOptions.forEach((sbo) => {
            const SmallBusinessOptionFormGroup = new SmallBusinessOptionForm();
            SmallBusinessOptionFormGroup.patchValue(sbo);
            this.selectedSmallBusinessOptionsFormArray.push(
              SmallBusinessOptionFormGroup
            );
          });
        }

        Object.assign(this, {
          StreetAddress,
          MailingAddress,
          RemitToAddress
        });

        if (this.StreetAddress) {
          const { address1, address2, city, state, zip } = this.StreetAddress;
          StreetAddressFormGroup.patchValue({
            VendorId: Vendor.id,
            ...(address1 && { address1 }),
            ...(address2 && { address2 }),
            ...(city && { city }),
            ...(state && { state: state as State }),
            ...(zip && { zip }),
            ...(country && { country })
          });
        }

        if (
          this.MailingAddress &&
          this.MailingAddress.id !== this.StreetAddress?.id
        ) {
          const { address1, address2, city, state, zip } = this.MailingAddress;
          MailingAddressFormGroup.patchValue({
            VendorId: Vendor.id,
            ...(address1 && { address1 }),
            ...(address2 && { address2 }),
            ...(city && { city }),
            ...(state && { state: state as State }),
            ...(zip && { zip }),
            ...(country && { country })
          });
        } else if (this.StreetAddress) {
          StreetAddressFormGroup.patchValue({ isMailing: true });
          MailingAddressFormGroup.patchValue({ isMailing: false });
        }

        if (
          this.RemitToAddress &&
          this.RemitToAddress.id !== this.StreetAddress?.id &&
          this.RemitToAddress.id !== this.MailingAddress?.id
        ) {
          const { address1, address2, city, state, zip } = this.RemitToAddress;
          RemitToAddressFormGroup.patchValue({
            VendorId: Vendor.id,
            ...(address1 && { address1 }),
            ...(address2 && { address2 }),
            ...(city && { city }),
            ...(state && { state: state as State }),
            ...(zip && { zip }),
            ...(country && { country })
          });
        } else if (this.StreetAddress && !this.MailingAddress) {
          StreetAddressFormGroup.patchValue({ isRemitTo: true });
          RemitToAddressFormGroup.patchValue({ isRemitTo: false });
        } else if (this.MailingAddress) {
          MailingAddressFormGroup.patchValue({ isRemitTo: true });
          RemitToAddressFormGroup.patchValue({ isRemitTo: false });
        }

        this.cdr.markForCheck();
      }
    });
  }
}
